import { Link, useNavigate } from "@tanstack/react-router"
import { Button } from "@/components/ui/button"
import { useApiQuery } from "@/api/ApiQueryServiceProvider"
import { buildGroupUri } from "@/hooks/useRemaUriBuilder"
import { ArrowLeft, Loader2, Edit, Users, Link2, ShieldCheck } from "lucide-react"
import { ProfileHeader, ProfileSection } from "@/components/sensnet/ProfileLayout"

interface GroupDetailPageProps {
  groupNumber: number | string
}

export function GroupDetailPage({ groupNumber }: GroupDetailPageProps) {
  const navigate = useNavigate()

  // reMa has no getGroupByNumber endpoint and getGroups returns the entire
  // table, so the record is found client-side (mirrors RoleDetailPage).
  const { data, isLoading, isError } = useApiQuery("getGroups", {})
  const group = ((data as any)?.content?.groups || (data as any)?.groups || []).find(
    (g: any) => String(g.groupNumber) === String(groupNumber)
  )

  const uri = group ? buildGroupUri({
    namespace: group.namespace ?? '',
    organization: group.organization ?? '',
    groupName: group.groupName ?? '',
  }) : ''

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="flex items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <span className="text-muted-foreground">Loading group details...</span>
        </div>
      </div>
    )
  }

  if (isError || !group) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <p className="text-sm text-muted-foreground">Group not found.</p>
        <Button variant="outline" onClick={() => navigate({ to: '/rema/groups' })}>Back to list</Button>
      </div>
    )
  }

  const roles: string[] = group.roles ?? []
  const members: string[] = group.members ?? []

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button asChild variant="ghost" size="sm" className="gap-2 -ml-2">
          <Link to="/rema/groups">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
        </Button>
        <Button asChild size="sm" className="gap-2">
          <Link to="/rema/groups/$groupNumber/edit" params={{ groupNumber: String(group.groupNumber) }}>
            <Edit className="h-4 w-4" />
            Edit
          </Link>
        </Button>
      </div>

      <ProfileHeader
        title={group.groupName}
        subtitle={`Group Number: ${group.groupNumber}`}
        subtitleIcon={<Users className="h-4 w-4" />}
        badge={group.namespace}
      />

      <ProfileSection title="Identity" icon={<Users className="h-4 w-4" />} variant="dark">
        <div className="space-y-3 p-5">
          <div>
            <p className="text-xs text-muted-foreground">Name</p>
            <p className="text-sm">{group.groupName}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Organization</p>
            <p className="text-sm">{group.organization}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Namespace</p>
            <p className="text-sm font-mono">{group.namespace}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Description</p>
            <p className="text-sm">{group.groupDescription || '—'}</p>
          </div>
        </div>
      </ProfileSection>

      <ProfileSection title="Assigned Roles" icon={<ShieldCheck className="h-4 w-4" />} variant="dark">
        <div className="space-y-2 p-5">
          {roles.length > 0 ? (
            roles.map((r) => (
              <div key={r} className="rounded-md border bg-muted/50 px-3 py-2">
                <code className="text-sm font-mono break-all">{r}</code>
              </div>
            ))
          ) : (
            <p className="text-sm text-muted-foreground">No roles assigned.</p>
          )}
        </div>
      </ProfileSection>

      <ProfileSection title="Members" icon={<Users className="h-4 w-4" />} variant="dark">
        <div className="space-y-2 p-5">
          {members.length > 0 ? (
            members.map((m) => (
              <div key={m} className="rounded-md border bg-muted/50 px-3 py-2">
                <span className="text-sm">{m}</span>
              </div>
            ))
          ) : (
            <p className="text-sm text-muted-foreground">No members.</p>
          )}
        </div>
      </ProfileSection>

      <ProfileSection title="Group URI" icon={<Link2 className="h-4 w-4" />} variant="dark">
        <div className="space-y-3 p-5">
          <div className="rounded-md border bg-muted/50 px-3 py-2">
            <code className="text-sm font-mono break-all">{uri || '—'}</code>
          </div>
        </div>
      </ProfileSection>
    </div>
  )
}
